export default {
    app: {
        name: 'ActivityWatch',
        researchEdition: 'Research Edition',
    },
    nav: {
        activity: 'Activity',
        loading: 'Loading...',
        noActivityReports: 'No activity reports available. Make sure you have both an AFK and window watcher running.',
        timeline: 'Timeline',
        stopwatch: 'Stopwatch',
        tools: 'Tools',
        search: 'Search',
        workReport: 'Work Report',
        aiSummary: 'AI Summary',
        trends: 'Trends',
        report: 'Report',
        alerts: 'Alerts',
        timespiral: 'Timespiral',
        query: 'Query',
        graph: 'Graph',
        rawData: 'Raw Data',
        settings: 'Settings',
    },
    footer: {
        madeWith: 'Made with',
        byDevs: 'by the ActivityWatch developers',
        host: 'Host:',
        version: 'Version:',
        reportBug: 'Report a bug',
        askHelp: 'Ask for help',
        voteFeatures: 'Vote on features',
        twitter: 'Twitter',
        github: 'GitHub',
        reddit: 'Reddit',
        donate: 'Donate',
    },
    common: {
        loading: 'Loading...',
        save: 'Save',
        cancel: 'Cancel',
        confirm: 'Confirm',
        import: 'Import',
        export: 'Export',
        open: 'Open',
        more: 'More',
        delete: 'Delete',
        enabled: 'Enabled',
        disabled: 'Disabled',
        home: 'Home',
        language: 'Language',
        languageUk: 'Українська',
        languageEn: 'English',
        languageDe: 'Deutsch',
        languageRu: 'Русский',
        languageSv: 'Svenska',
        languageZhCN: '简体中文',
        rangeTo: 'to',
    },
    notFound: {
        title: 'Oops, this page was not found!',
        hint: 'Try navigating back where you came from.',
    },
    settings: {
        title: 'Settings',
        unsavedCategoriesLeave: 'Your categories have unsaved changes, are you sure you want to leave?',
        daystart: {
            startOfDay: 'Start of day',
            startOfDayHelp: 'The time at which days "start", since humans do not always go to bed before midnight. Set to 04:00 by default.',
            startOfWeek: 'Start of week',
            startOfWeekHelp: 'The weekday which starts a new week.',
            saturday: 'Saturday',
            sunday: 'Sunday',
            monday: 'Monday',
        },
        timelineDuration: {
            title: 'Duration default value',
            help: "The default duration used for 'show last' in the timeline view.",
        },
        landingPage: {
            title: 'Landing page',
            home: 'Home',
            activity: 'Activity ({hostname})',
            timeline: 'Timeline',
            help: 'The page to open when opening ActivityWatch, or clicking the logo in the top menu.',
        },
        theme: {
            title: 'Theme',
            auto: 'Auto (System)',
            light: 'Light',
            dark: 'Dark',
            help: 'Change the color theme. Category colors are picked separately — you may want to adjust them when switching to dark mode.',
        },
        groups: {
            general: 'General',
            generalHelp: 'Defaults that shape how time periods, the timeline, and landing page behave.',
            appearance: 'Appearance',
            appearanceHelp: 'Theme and visualization colors.',
            categorization: 'Categorization',
            categorizationHelp: 'Rules that classify events into categories, plus AFK/active-pattern overrides.',
            privacy: 'Privacy',
            privacyHelp: 'Filters that drop or redact sensitive event data before it is stored.',
            developer: 'Developer',
        },
        language: {
            title: 'Language',
            help: 'Interface language for the web UI.',
        },
        releaseNotification: {
            title: 'Check for new releases',
            help: 'When enabled, the web UI checks once per day for a new ActivityWatch release and shows a hint if one is available.',
        },
        color: {
            title: 'Use fallback colors',
            help: 'Uses the old coloring style for some visualizations when uncategorized or no category color.',
        },
        activePattern: {
            title: 'Always count as active pattern',
            help1: 'Apps or titles matching this regular expression will never be counted as AFK.',
            help2: 'Can be used to count time as active, despite no input (like meetings, or games with controllers). An empty string disables it.',
            example: 'Example expression:',
            invalid: 'Invalid pattern',
            tooBroad: 'Pattern too broad',
        },
        categorization: {
            title: 'Categorization',
            restoreDefaults: 'Restore defaults',
            rulesHelp: 'Rules for categorizing events. An event can only have one category. If several categories match, the deepest one will be chosen.',
            builderIntro: 'You can use the',
            categoryBuilder: 'Category Builder',
            builderOutro: 'to quickly create categories from uncategorized activity.',
            forumIntro: 'You can also find and share categorization rule presets on',
            forum: 'the forum',
            docsIntro: 'For help on how to write categorization rules, see',
            documentation: 'the documentation',
            categorySet: 'Category set:',
            newSet: 'New set',
            deleteSet: 'Delete set',
            setsAvailable: '{count} sets available — switch sets to use different rule profiles.',
            unsavedChanges: 'You have unsaved changes!',
            discard: 'Discard',
            addCategory: 'Add category',
            categories: 'Categories',
            priority: 'Priority',
            priorityDefault: 'Default',
            priorityInteger: 'Priority must be an integer.',
            builderTitle: 'Category builder',
            builderSubtitle: 'Generate rules from uncategorized activity',
            openBuilder: 'Open builder',
            hideBuilder: 'Hide builder',
        },
        developer: {
            title: 'Developer settings',
            note: 'Note:',
            noteBody: 'These settings are meant for developers who (hopefully) know what they are doing, and as such, may break things unexpectedly.',
            forceDevmode: 'Force devmode',
            forceDevmodeHelp: 'Devmode enables some features that are still work-in-progress.',
            requestTimeout: 'Request timeout',
            requestTimeoutHelp: 'The maximum amount of time a server request can take before timing out. Setting this to a high value can be useful for large queries. Note that you need to reload the web UI for it to apply.',
            commitHash: 'Web UI commit hash:',
        },
    },
    home: {
        note: 'Note:',
        androidNote: 'ActivityWatch on Android is in a very early stage of development. There will be bugs, but we hope you bear with us as we refine things and get it on par with the desktop version of ActivityWatch (which you should try!).',
        greeting: 'Welcome to ActivityWatch',
        intro1: "We've come a long way{android} but we still need users (like you!) to provide feedback and help us turn ActivityWatch into a successful project. We'd love to hear any ideas you have for improvements.",
        introAndroid: ' (especially on Android!)',
        intro3: 'If you are a developer, we hope you can contribute by writing a watcher, visualization, or something else, and share it with us on the forum!',
        thanks: 'Thank you for using ActivityWatch!',
        surveyFill: 'Fill out our user survey',
        surveySuffix: 'to help shape what comes next.',
        surveyOr: 'or',
        voteForum: 'vote on features on the forum',
        spreadTitle: 'Spread the word',
        spread1: 'Nothing is as motivating as getting ActivityWatch into the hands of users. By sharing it you get us to make ActivityWatch even better!',
        spreadReview: 'Give us a like and a review on',
        spreadVote: 'Vote on us at',
        spreadFollow: 'Follow us and spread the word on',
        spreadStar: 'Star us on',
        supportTitle: 'Support us!',
        subscribeIntro: 'The best way to keep ActivityWatch maintained is a subscription — from $5/month, every feature stays free, cancel anytime.',
        subscribeCta: 'Support ActivityWatch →',
        support1: 'Do you like ActivityWatch? Has it helped you? Help us help you by donating! You can donate to us via:',
        support2: 'For more info, please visit the',
        donationPage: 'donation page on the website',
        otherDonationMethods: 'Other methods (incl. cryptocurrency)',
        resourcesTitle: 'Resources',
        website: 'Website',
        documentation: 'Documentation',
        forum: 'Forum',
        discord: 'Discord',
        reddit: 'Reddit',
        apiBrowser: 'API Browser',
        workingOnTitle: "What we're working on",
        workingOnPrefix: 'Stay up to date with the',
        devUpdates: 'development updates',
        workingOnMiddle: 'and follow the project on',
        workingOnSuffix: 'for release notes.',
        landingHint: 'You can change which page opens when you open ActivityWatch (instead of this page) in the',
        supporterNudge: {
            message: "You've been getting a lot out of ActivityWatch — would you consider supporting its development?",
            support: 'Support ActivityWatch →',
            notNow: 'Not now',
        },
    },
    buckets: {
        title: 'Buckets',
        moreWatchers: 'Are you looking to collect more data? Check out',
        docsLink: 'the docs',
        thisDevice: 'this device',
        lastUpdatedInline: 'Last updated',
        noEventsYet: 'No events recorded yet',
        noEvents: 'no events',
        moreActionsFor: 'More actions for {hostname}',
        deleteAllTitle: 'Delete all {count} buckets for {hostname}',
        deleteAllForHost: 'Delete all buckets for this host',
        openBucket: 'Open bucket',
        deleteBucketTitle: 'Delete bucket?',
        deleteHostTitle: 'Delete buckets?',
        deleteHostTitleNamed: 'Delete all buckets for {hostname}?',
        deleteHostConfirmPrefix: 'Are you sure you want to delete',
        deleteHostConfirmCount: 'all {count} buckets',
        deleteHostConfirmSuffix: 'for host "{hostname}"?',
        bucketsToDelete: 'Buckets that will be deleted:',
        deleting: 'Deleting...',
        importing: 'Importing...',
        importHelpNew: 'Provide a JSON file exported from a single bucket or from multiple buckets. Import fails if a bucket with the same ID already exists.',
        exportHelp: 'Download every bucket on this server as a single JSON file. Use this for backups.',
        currentDevice: '(the current device)',
        lastUpdated: 'Last updated:',
        firstSeen: 'First seen:',
        bucketId: 'Bucket ID',
        updated: 'Updated',
        exportBucketJson: 'Export bucket as JSON',
        exportEventsCsv: 'Export events as CSV',
        deleteBucket: 'Delete bucket',
        deleteModalTitle: 'Danger!',
        deleteConfirm: 'Are you sure you want to delete bucket "{id}"?',
        deletePermanent: 'This is permanent and cannot be undone!',
        importExportTitle: 'Import and export buckets',
        importBuckets: 'Import buckets',
        importPlaceholder: 'Choose or drop a file here...',
        importDrop: 'Drop file here...',
        importHelp: 'A valid file to import is a JSON file from either an export of a single bucket or an export from multiple buckets. If there are buckets with the same name the import will fail.',
        exportBuckets: 'Export buckets',
        exportAllJson: 'Export all buckets as JSON',
        exporting: 'Exporting...',
        tools: 'Tools',
    },
    activity: {
        title: 'Activity',
        browserAllowlistMiss: 'No matching browser window for this period. If you were browsing in a Chromium/Firefox fork, its app name may not be recognized yet.',
        for: 'for',
        host: 'Host:',
        devices: 'Devices:',
        allDevices: 'All devices',
        allDevicesCount: 'All devices ({count})',
        onlyThisDevice: 'only',
        multideviceNote: 'Browser and stopwatch data are only available when viewing a single device.',
        timeActive: 'Time active:',
        queryRange: 'Query range:',
        filters: 'Filters',
        filtersTitle: 'Filters',
        refresh: 'Refresh',
        excludeAfk: 'Exclude AFK time',
        audibleActive: 'Count audible browser tab as active',
        includeStopwatch: 'Include manually logged events (stopwatch)',
        newView: 'New view',
        loadDemo: 'Load demo data',
        periodDay: 'day',
        periodWeek: 'week',
        periodMonth: 'month',
        periodYear: 'year',
        periodLast7d: '7 days',
        periodLast30d: '30 days',
        periodLast7dTitle: 'last 7 days',
        periodLast30dTitle: 'last 30 days',
        periodCustomRange: 'custom range',
        rangeStart: 'Start date',
        rangeEnd: 'End date',
        invalidRange: 'Invalid date range in URL, showing today instead.',
        periodAllTime: 'all time',
        allTimeSlowHint: 'All time can take a while on large databases.',
        filterAfkTooltip: 'Filter away time where the AFK watcher did not detect any input.',
        filterAudibleTooltip: 'If the active window is an audible browser tab, count as active. Requires a browser watcher.',
        showCategory: 'Show category',
        newViewTitle: 'New view',
        viewId: 'ID',
        viewName: 'Name',
        stopwatchWipNote: 'Note: WIP. Stopwatch events shadow other events when overlapping. Only shown in devmode.',
        invalidForm: 'Invalid form input: {errors}',
        errIdNotUnique: 'ID is not unique',
        errMissingId: 'Missing ID',
        errMissingName: 'Missing name',
    },
    activityView: {
        addVisualization: 'Add visualization',
        cancel: 'Cancel',
        save: 'Save',
        restoreDefaults: 'Restore defaults',
        remove: 'Remove',
        editView: 'Edit view',
        restoreAlert: "All views have been restored to defaults. Changes won't be saved until you click Save.",
        removeViewTitle: 'Remove this view?',
        removeViewOk: 'Remove view',
        removeViewConfirm: 'Are you sure you want to remove',
        removeViewHint: "This will delete the view's configuration. You can run",
        removeViewRestoreHint: 'to bring built-in views back.',
        promptWatcher: 'Please enter the watcher name',
        promptTitle: 'Please enter the visualization title',
    },
    uncategorized: {
        title: 'High uncategorized time',
        body: 'You have a total of {duration} uncategorized time, that is {percent}% of all time {period}.',
        hint: 'You can address this by using the',
        categoryBuilder: 'Category Builder',
        settingsCogTitle: 'Hide or adjust this hint in Settings',
        periodToday: 'today',
        periodThisWeek: 'this week',
        periodThisMonth: 'this month',
        periodThisYear: 'this year',
        periodLast7d: 'the last 7 days',
        periodLast30d: 'the last 30 days',
        periodCustomRange: 'in the selected range',
        periodAllTime: 'in total',
    },
    visualizations: {
        noData: '(no data)',
        unsupportedPeriod: "This feature doesn't support the current time period.",
        missingWatcher: 'This feature is missing data from a required watcher.',
        docLink: 'documentation',
        allCategories: 'All',
        topApps: 'Top Applications',
        topTitles: 'Top Window Titles',
        topDomains: 'Top Browser Domains',
        topUrls: 'Top Browser URLs',
        topBrowserTitles: 'Top Browser Titles',
        topEditorFiles: 'Top Editor Files',
        topEditorLanguages: 'Top Editor Languages',
        topEditorProjects: 'Top Editor Projects',
        topCategories: 'Top Categories',
        categoryTree: 'Category Tree',
        categorySunburst: 'Category Sunburst',
        timelineBarchart: 'Timeline (barchart)',
        sunburstClock: 'Sunburst clock',
        dailyTimeline: 'Daily Timeline (Chronological)',
        customVis: 'Custom Visualization',
        score: 'Score',
        topStopwatches: 'Top Stopwatch Events',
        topBucketData: 'Top Bucket Data',
    },
    timeline: {
        title: 'Timeline',
        eventsShown: 'Events shown:',
        swimlanes: 'Swimlanes:',
        filterAfk: 'Filter AFK',
        mergeByApp: 'Merge by app',
        noEvents: 'No events match selected criteria. Timeline is not updated.',
        scrollHint: 'Scroll to zoom, swipe/horizontal-scroll to pan, arrow keys to navigate',
        loading: 'Loading...',
    },
    stopwatch: {
        title: 'Stopwatch',
        usingBucket: 'Using bucket:',
        aboutTitle: 'About the stopwatch',
        aboutBody: 'Track manually-logged sessions alongside automatic tracking. To see your stopwatch totals on a dashboard, open an Activity view, click Edit view, then Add visualization and pick Top Stopwatch Events.',
        placeholder: 'What are you working on?',
        noRunning: 'No stopwatch running. Start one above to track focused work.',
        start: 'Start',
        running: 'Running',
        history: 'History',
    },
    search: {
        title: 'Search',
        earlyDev: 'This feature is still in early development.',
        searchBtn: 'Search',
        options: 'Options',
        notFound: "Didn't find what you were looking for?",
        addWeek: 'Add a week to the search:',
        addWeekBtn: '+1 week',
    },
    query: {
        title: 'Query Explorer',
        docsHelp: 'See the documentation for help on how to write queries.',
        start: 'Start',
        end: 'End',
    },
    workReport: {
        title: 'Work Time Report',
        loading: 'Loading...',
    },
    report: {
        title: 'Report',
        description: 'Generate a report of time spent on a certain category of device activity.',
        earlyDev: 'This feature is still in early development.',
        generate: 'Generate',
        options: 'Options',
        foundEvents: 'Found {count} events in {seconds} seconds',
        csv: 'CSV',
    },
    trends: {
        titlePrefix: 'Trends for',
        earlyDev: 'This feature is still in early development.',
        loadDemo: 'Load demo data',
    },
    timespiral: {
        title: 'Timespiral',
        wip: 'This is a work-in-progress experiment.',
        noAfkBucket: 'No AFK bucket found on this host. Install',
        noAfkBucketSuffix: 'to use the Timespiral.',
        bucketLabel: 'Bucket:',
        eventsLabel: 'Events:',
    },
};
